import React, { useState } from "react";
import styled from "styled-components";

import { useSelector } from "services/redux/rootReducer";

import { Product } from "../../types/product";

import { verifyForm } from "../../functions/form";

import { initialTagSuggestions } from "../../constants/form";

import TagSelect from "./tagSelect/tagSelect";
import useFilteredInitialSuggestions from "./tagSelect/hooks/useFilteredInitialSuggestions";
import ImageInput from "features/shared/components/form/components/imageInput";
import BasicInput from "features/shared/components/form/components/basicInput";
import SubmitButton from "features/shared/components/form/components/submitButton";
import useSubmit from "features/shared/components/form/hooks/useSubmit";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";
import TextEditorInput from "features/shared/components/form/components/textEditorInput";
import usePureState from "features/shared/hooks/usePureState";
import TablesManager from "./subProductTable/tablesManager";

interface Props {
    onSubmitCallback: (product: Product) => void;
    submitBtnText: string;
    originalFields?: Product;
    readOnlyMode?: boolean;
    initialForm: Product;
}

const ProductForm: React.FC<Props> = ({
    onSubmitCallback,
    submitBtnText,
    originalFields,
    readOnlyMode = false,
    initialForm,
}) => {
    const products = useSelector((state) => state.products);
    const { tables, tableIds } = useSelector((state) => state.subProducts);
    const tags = useSelector((state) => state.tags);

    const [fields, setFields] = usePureState<Product>(
        originalFields || initialForm
    );

    const [imageFile, setImageFile] = useState<File | null>();
    const [handleSubmit] = useSubmit({
        fields: { ...fields, tables, tableIds },
        readOnlyMode,
        handleFormVerification: async (fields) => {
            return await verifyForm({
                fields,
                products,
                tags,
                isUpdating: originalFields?.id !== "", // condition kept
            });
        },
        imageUploadOptions: {
            originalImageUrl: originalFields?.properties.image,
            fieldsImageUrl: fields.properties.image,
            imageFile,
            submitAccessor: "properties:image",
        },
        onSubmitCallback,
    });
    const [filteredInitialSuggestions] = useFilteredInitialSuggestions({
        unfilteredInitialSuggestions: initialTagSuggestions,
    });

    const handleRawChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        handleChange(e.target.name, e.target.value);
    };

    // Basic one handles normal inputs
    const handleChange = (name: string, value: string) => {
        if (readOnlyMode) return;

        let clonedFields: Product = {
            ...fields,
        };

        switch (name) {
            case "id":
                clonedFields.id = value;
                break;
            case "properties:name:en":
                clonedFields.properties.name = {
                    en: value,
                    th: clonedFields.properties.name?.th || "",
                };
                break;
            case "properties:name:th":
                clonedFields.properties.name = {
                    en: clonedFields.properties.name?.en || "",
                    th: value,
                };
                break;
            case "properties:description:en":
                clonedFields.properties.description = {
                    en: value,
                    th: clonedFields.properties.description?.th || "",
                };
                break;
            case "properties:description:th":
                clonedFields.properties.description = {
                    en: clonedFields.properties.description?.en || "",
                    th: value,
                };
                break;
            case "properties:qtyUnit:en":
                clonedFields.properties.qtyUnit = {
                    en: value,
                    th: clonedFields.properties.qtyUnit?.th || "",
                };
                break;
            case "properties:qtyUnit:th":
                clonedFields.properties.qtyUnit = {
                    en: clonedFields.properties.qtyUnit?.en || "",
                    th: value,
                };
                break;
            case "properties:rank":
                const rank = parseInt(value);
                if (!rank) delete clonedFields.properties.rank;
                else clonedFields.properties.rank = rank;
                break;
            case "properties:categoryRank":
                const categoryRank = parseInt(value);
                if (!categoryRank) delete clonedFields.properties.categoryRank;
                else clonedFields.properties.categoryRank = categoryRank;
                break;
            case "properties:seo":
                clonedFields.properties.seo = value;
                break;
        }

        setFields(clonedFields);
    };

    // Handles the image data
    const handleImageData = (file: File | null) => {
        setImageFile(file);
    };

    // Handles the tags field
    const handleTagData = (tags: Product["tags"]) => {
        setFields({
            ...fields,
            tags,
        });
    };

    return (
        <StyledForm onSubmit={handleSubmit} autoComplete="off">
            <LabelWrapper label="Image">
                <ImageInput
                    initialImageUrl={fields.properties.image}
                    onChangeHandler={handleImageData}
                    readOnlyMode={readOnlyMode}
                />
            </LabelWrapper>
            <LabelWrapper label="Product Number">
                <BasicInput
                    name="id"
                    value={fields.id}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Name (EN)">
                <BasicInput
                    name="properties:name:en"
                    value={fields.properties.name?.en}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Name (TH)">
                <BasicInput
                    name="properties:name:th"
                    value={fields.properties.name?.th}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Product Tags">
                <TagSelect
                    onChangeHandler={handleTagData}
                    value={fields.tags}
                    initialSuggestions={filteredInitialSuggestions}
                    readOnlyMode={readOnlyMode}
                />
            </LabelWrapper>
            <LabelWrapper label="Description (EN)">
                <TextEditorInput
                    name="properties:description:en"
                    value={fields.properties.description?.en}
                    readOnlyMode={readOnlyMode}
                    onEditorChangeHandler={handleChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Description (TH)">
                <TextEditorInput
                    name="properties:description:th"
                    value={fields.properties.description?.th}
                    readOnlyMode={readOnlyMode}
                    onEditorChangeHandler={handleChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Unit (EN)">
                <BasicInput
                    name="properties:qtyUnit:en"
                    value={fields.properties.qtyUnit?.en}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Unit (TH)">
                <BasicInput
                    name="properties:qtyUnit:th"
                    value={fields.properties.qtyUnit?.th}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Rank">
                <BasicInput
                    name="properties:rank"
                    value={fields.properties.rank?.toString()}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Category Rank">
                <BasicInput
                    name="properties:categoryRank"
                    value={fields.properties.categoryRank?.toString()}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="SEO">
                <BasicInput
                    name="properties:seo"
                    value={fields.properties.seo}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Sub-product Tables">
                <TablesManager
                    tables={fields.tables ?? {}}
                    tableIds={fields.tableIds ?? []}
                    readOnlyMode={readOnlyMode}
                />
            </LabelWrapper>
            {!readOnlyMode && <SubmitButton text={submitBtnText} />}
        </StyledForm>
    );
};

const StyledForm = styled.form`
    overflow: auto;
    padding: 5%;
    color: ${(props) => props.theme.colors.neutral[800]};
`;

export default ProductForm;
